import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Wallet } from "lucide-react";
import { papi, errText } from "../portalApi";
import { Card, EmptyState, Modal, PageHeader, Spinner, StatusBadge, fmtDate, fmtMoney } from "../ui";

const Row = ({ label, value, bold, negative }) => (
  <div className="flex items-center justify-between border-b border-navy-900/5 py-2.5 last:border-0">
    <p className={`text-sm ${bold ? "font-semibold text-navy-900" : "text-slate-500"}`}>{label}</p>
    <p className={`text-sm ${bold ? "font-serif text-lg text-navy-900" : "font-semibold"} ${negative ? "text-rose-600" : "text-navy-900"}`}>
      {negative ? "−" : ""}{fmtMoney(value)}
    </p>
  </div>
);

export default function Payslips() {
  const [records, setRecords] = useState(null);
  const [active, setActive] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await papi.get("/payroll");
      setRecords(r.data);
    } catch (err) {
      toast.error(errText(err, "Could not load payslips."));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!records) return <Spinner />;

  const openSlip = async (id) => {
    try {
      const r = await papi.get(`/payroll/${id}`);
      setActive(r.data);
    } catch (err) {
      toast.error(errText(err, "Could not open the payslip."));
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await papi.get(`/payroll/${active.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `RCS-Payslip-${active.month}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-testid="payslips-page">
      <PageHeader title="Payslips" sub="Your salary and stipend statements, month by month." />

      {records.length === 0 ? (
        <EmptyState
          icon={Wallet}
          title="No payslips yet"
          sub="Once payroll processes your first salary or stipend, the statement will appear here."
          testid="payslips-empty"
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="payslips-list">
          {records.map((r) => (
            <button
              key={r.id}
              onClick={() => openSlip(r.id)}
              className="rounded-2xl border border-navy-900/10 bg-white p-6 text-left shadow-[0_1px_3px_rgba(11,19,43,0.05)] transition-all duration-300 hover:-translate-y-0.5 hover:border-gold/50"
              data-testid={`payslip-${r.id}`}
            >
              <div className="flex items-center justify-between">
                <p className="font-serif text-lg text-navy-900">{r.month}</p>
                <StatusBadge status={r.status} />
              </div>
              <p className="mt-1 text-xs uppercase tracking-wider text-slate-400">{r.compensationType}</p>
              <p className="mt-4 font-serif text-3xl text-navy-900">{fmtMoney(r.net)}</p>
              <p className="mt-1 text-[11px] text-slate-400">Net payable</p>
            </button>
          ))}
        </div>
      )}

      <Modal open={Boolean(active)} onClose={() => setActive(null)} title={active ? `Payslip — ${active.month}` : ""} wide>
        {active && (
          <div data-testid="payslip-detail">
            <div className="rounded-xl bg-navy-950 px-6 py-5 text-slate-300">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-serif text-lg text-ivory">Roy Career Solutions Private Limited</p>
                  <p className="mt-0.5 text-[11px] uppercase tracking-[0.2em] text-gold">{active.compensationType} Statement</p>
                </div>
                <StatusBadge status={active.status} />
              </div>
              <div className="mt-5 grid grid-cols-2 gap-4 text-xs sm:grid-cols-4">
                {[
                  ["Employee", active.employeeName],
                  ["Employee ID", active.employeeCode],
                  ["Designation", active.designation || "—"],
                  ["Department", active.department || "—"],
                ].map(([k, v]) => (
                  <div key={k}>
                    <p className="text-slate-500">{k}</p>
                    <p className="mt-0.5 font-semibold text-ivory">{v}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 grid gap-6 sm:grid-cols-2">
              <div>
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gold-dark">Earnings</p>
                <Row label="Gross" value={active.gross} />
                <Row label="Incentives" value={active.incentives} />
                <Row label="Reimbursements" value={active.reimbursements} />
                <Row label="Adjustments" value={active.adjustments} />
              </div>
              <div>
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gold-dark">Deductions</p>
                <Row label="Deductions" value={active.deductions} negative />
                <div className="mt-4 rounded-xl bg-gold/10 px-4 py-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-navy-900">Net Payable</p>
                    <p className="font-serif text-2xl text-navy-900" data-testid="payslip-net">{fmtMoney(active.net)}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-navy-900/10 pt-4 text-xs text-slate-500">
              <div className="flex flex-wrap gap-6">
                <p>Payment date: <span className="font-semibold text-navy-900">{active.paymentDate ? fmtDate(active.paymentDate) : "—"}</span></p>
                <p>Reference: <span className="font-semibold text-navy-900">{active.referenceNumber || "—"}</span></p>
              </div>
              <button
                onClick={downloadPdf}
                disabled={downloading}
                className="btn-outline-dark !px-5 !py-2 !text-xs disabled:opacity-60"
                data-testid="payslip-download-pdf"
              >
                <Download className="h-3.5 w-3.5" aria-hidden /> {downloading ? "Preparing…" : "Download PDF"}
              </button>
            </div>
            <p className="mt-4 text-[10px] leading-relaxed text-slate-400">
              This is a system-generated statement from the RCS Employee Portal. For queries, contact HR or Payroll.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}
